import { useCallback, useEffect, useRef, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { groupTripErrorMessage, isGroupTripCapacityExceededError } from './groupTripModel';

const EMPTY_TRAVELERS = Object.freeze([]);
const body = (value) => value?.data?.data ?? value?.data ?? value;
const hasHttpResponse = cause => Number(cause?.httpStatus ?? cause?.response?.status) > 0;
const rows = (value) => { const v = body(value); return Array.isArray(v) ? v : v?.items || []; };

/** 只增加家庭代表申請／主揪審核入口，不建立帳號，也不自動發 Claim。 */
export default function GroupTripPartyAdditionSection({ groupTripId, viewerId, travelers = EMPTY_TRAVELERS, isHost = false, onChanged }) {
  const { t } = useTravelTranslation();
  const [parties, setParties] = useState([]);
  const scope = `${groupTripId}:${viewerId}:${isHost}`;
  const [loadedFor, setLoadedFor] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const current = ++generation.current;
    if (typeof groupTripApi.listParties !== 'function') return;
    setLoading(true);
    try {
      const all = rows(await groupTripApi.listParties(groupTripId));
      const allowed = all.filter(p => isHost || travelers.some(v => String(v.travelerId) === String(p.representativeTravelerId) && String(v.linkedMemberId) === String(viewerId) && v.status === 'ACTIVE'));
      const data = await Promise.all(allowed.map(async p => ({ ...p, additions: rows(await groupTripApi.listPartyAdditions(groupTripId, p.partyId)) })));
      if (current === generation.current) { setParties(data); setLoadedFor(scope); setError(''); }
    } catch (cause) { if (current === generation.current) setError(groupTripErrorMessage(cause, t, 'travel.partyAddition.failed')); }
    finally { if (current === generation.current) setLoading(false); }
  }, [groupTripId, viewerId, travelers, isHost, scope, t]);
  useEffect(() => {
    setParties([]); setError(''); load();
    return () => { generation.current += 1; };
  }, [load]);
  if (typeof groupTripApi.listParties !== 'function') return null;
  return <section className="group-trips__panel" aria-label={t('travel.partyAddition.title')}>
    <h2>{t('travel.partyAddition.title')}</h2>
    <p>{t('travel.partyAddition.hint')}</p>
    {loading && <p role="status">{t('travel.partyAddition.loading')}</p>}
    {error && <p role="alert">{error}</p>}
    <button type="button" onClick={load}>{t('travel.partyAddition.refresh')}</button>
    {(loadedFor === scope ? parties : []).map(p => <PartyAddition key={`${viewerId}-${groupTripId}-${p.partyId}`} party={p} groupTripId={groupTripId} isHost={isHost} reload={load} onChanged={onChanged} />)}
  </section>;
}

function PartyAddition({ party, groupTripId, isHost, reload, onChanged }) {
  const { t } = useTravelTranslation();
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unknown, setUnknown] = useState(false);
  const pending = useRef(null);
  const flight = useRef(false);
  const decision = useRef(null);
  async function run(action) {
    if (flight.current) return;
    flight.current = true; setBusy(true); setError('');
    try { await action(); }
    catch (cause) { setError(isGroupTripCapacityExceededError(cause) ? t('travel.partyAddition.capacity') : groupTripErrorMessage(cause, t, 'travel.partyAddition.failed')); }
    finally { flight.current = false; setBusy(false); }
  }
  async function submit(event) {
    event.preventDefault();
    if (!name.trim() || unknown) return;
    await run(async () => {
      if (!pending.current) pending.current = { clientRequestId: crypto.randomUUID(), displayName: name.trim(), reason: reason.trim() };
      try { await groupTripApi.requestPartyAddition(groupTripId, party.partyId, pending.current); }
      catch (cause) {
        // 無 HTTP 結果可能已提交，不換識別碼、不自動重送。
        if (!hasHttpResponse(cause)) { setUnknown(true); setError(t('travel.partyAddition.unknown')); return; }
        pending.current = null; throw cause;
      }
      pending.current = null; setName(''); setReason(''); await reload();
    });
  }
  async function reconcile() {
    await run(async () => {
      const all = rows(await groupTripApi.listPartyAdditions(groupTripId, party.partyId));
      const confirmed = decision.current
        ? all.some(r => r.requestId === decision.current && r.status !== 'PENDING')
        : all.some(r => r.clientRequestId === pending.current?.clientRequestId);
      if (confirmed) {
        decision.current = null;
        pending.current = null; setUnknown(false); setName(''); setReason(''); await reload();
      } else setError(t('travel.partyAddition.unknown'));
    });
  }
  async function decide(row, action) {
    await run(async () => {
      try { await groupTripApi.decidePartyAddition(groupTripId, party.partyId, row.requestId, action); }
      catch (cause) {
        if (!hasHttpResponse(cause)) { decision.current = row.requestId; setUnknown(true); setError(t('travel.partyAddition.unknown')); return; }
        throw cause;
      }
      await reload(); if (onChanged) await onChanged();
    });
  }
  return <div className="group-trips__card">
    <h3>{party.partyName}</h3>
    <p>{t('travel.partyAddition.current')}: {party.memberTravelerIds?.length || 0}</p>
    {!isHost && party.status === 'ACTIVE' && <form onSubmit={submit}>
      <label>{t('travel.partyAddition.name')}<input required maxLength={40} value={name} disabled={busy || unknown} onChange={e => { setName(e.target.value); pending.current = null; }} /></label>
      <label>{t('travel.partyAddition.reason')}<input maxLength={200} value={reason} disabled={busy || unknown} onChange={e => { setReason(e.target.value); pending.current = null; }} /></label>
      <button type="submit" disabled={busy || unknown || !name.trim()}>{t('travel.partyAddition.request')}</button>
    </form>}
    {unknown && <button type="button" disabled={busy} onClick={reconcile}>{t('travel.partyAddition.check')}</button>}
    {error && <p role="alert">{error}</p>}
    <ul>{party.additions.map(row => <li key={row.requestId}>
      <p>{row.displayName} · {t(`travel.partyAddition.${row.status}`)}</p>
      {row.status === 'PENDING' && <div className="group-trips__actions">
        {isHost ? <><button type="button" disabled={busy || unknown} onClick={() => decide(row, 'approve')}>{t('travel.partyAddition.approve')}</button><button type="button" disabled={busy || unknown} onClick={() => decide(row, 'reject')}>{t('travel.partyAddition.reject')}</button></> : <button type="button" disabled={busy || unknown} onClick={() => decide(row, 'cancel')}>{t('travel.partyAddition.cancel')}</button>}
      </div>}
    </li>)}</ul>
  </div>;
}
